import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetResellerTranscripts } from '@utils/api/resellers/use-get-reseller-transcripts.tsx';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { formatDate, formatDuration } from '@utils/date.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

type TranscriptionsDetailsProps = {
  resellerId?: string;
};

type Row = {
  session_id: string;
  filename: string;
  language_label: string;
  last_update_label: string;
  status_label: string;
  date_session_start_label?: string;
  duration_label?: string;
};

export const TranscriptionsDetails = ({
  resellerId
}: TranscriptionsDetailsProps) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { data, isLoading, isFetching, error } = useGetResellerTranscripts(
    resellerId || ''
  );
  const { data: supportedLanguages } = useGetModels();

  const isForbidden = error?.response?.status === 403;

  const mapLanguage = (code?: string | null) => {
    if (!code) return '-';
    const key = code.split('-')[0]?.toLowerCase();
    const lang = supportedLanguages?.result.find(l => l.key === key);
    return lang?.name ?? code;
  };

  const safeFormatDate = (d?: string | null) => {
    if (!d) return '-';
    const time = new Date(d).getTime();
    return Number.isNaN(time) ? '-' : formatDate(d);
  };

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'session_id',
        accessorKey: 'session_id',
        header: t('session-id'),
        minWidth: 240,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'filename',
        accessorKey: 'filename',
        header: t('filename'),
        minWidth: 240,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'language_label',
        accessorKey: 'language_label',
        header: t('language'),
        minWidth: 120,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'last_update_label',
        accessorKey: 'last_update_label',
        header: t('last-modified'),
        minWidth: 180,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'status_label',
        accessorKey: 'status_label',
        header: t('status'),
        minWidth: 100,
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '-')
      }
    ],
    [t]
  );

  const rows: Row[] = useMemo(() => {
    const list = data?.result ?? [];
    return list.map(tr => ({
      session_id: tr.session_id,
      filename: tr.filename,
      language_label: mapLanguage(tr.language),
      last_update_label: safeFormatDate(tr.last_update || ''),
      status_label: tr.status === 'finished' ? t('completed') : t('error'),
      date_session_start_label: safeFormatDate(tr.date_session_start || ''),
      duration_label: tr.duration != null ? formatDuration(tr.duration) : '-'
    }));
  }, [data, supportedLanguages, t]);

  if (isForbidden) {
    return (
      <div className="px-4 py-8">
        <div
          className="flex flex-col items-center justify-center py-12 rounded-2xl border"
          style={{
            backgroundColor: getColour('baseLayer'),
            borderColor: getColour('border')
          }}
        >
          <AlertTriangle
            className="h-10 w-10 mb-3"
            style={{ color: getColour('inactiveText') }}
          />
          <p
            className="text-sm font-medium mb-1"
            style={{ color: getColour('normalText') }}
          >
            {t('no-transcript-access')}
          </p>
          <p
            className="text-xs text-center max-w-md"
            style={{ color: getColour('inactiveText') }}
          >
            {t('no-transcript-access-description')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <DataGridV2<Row>
        data={rows}
        columns={columns}
        enableRowSelection={false}
        enablePagination
        manualPagination
        enableSorting={false}
        manualSorting={false}
        enableFiltering
        manualFiltering
        showColumnFilters={false}
        filterMode="popover"
        totalItems={data?.total_results ?? 0}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        height="55vh"
        dense
        grid={false}
        striped
        loading={isLoading || isFetching || !data}
        getRowId={row => row.session_id}
      />
    </div>
  );
};
